import { useDispatch, useSelector } from "react-redux";
import ProfileCard from "../../../components/profile-card";
import Grid from "@mui/material/Grid";
import { useEffect, useCallback, useRef } from "react";
import { profileReq } from "../../../store/slices/profileSlice";
import { DashboardSelector } from "./selector";
import PageWrapper from "../../../components/page-wrapper";
import Search from "../../../components/forms/search";
import { useNavigate, useLocation } from "react-router-dom";
import Api from "../../../store/api";
import { Box, FormControlLabel, Switch, Typography } from "@mui/material";
import { showToast } from "../../../utils/toast";

export default function Dashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const RS = useSelector(DashboardSelector);
  const hasProfileImageOnly = !!RS.hasProfileImage;
  const isSearchPage = location.pathname === "/user/search";
  const isViewersPage = location.pathname === "/user/viewers";
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Detect current path and update list based on it
  useEffect(() => {
    let listName = "all";
    if (location.pathname === "/user/search") {
      listName = "search";
    } else if (location.pathname === "/user/viewers") {
      listName = "viewers";
    }
    
    // Load list if not already loaded for this listName
    if (RS.listName !== listName || RS.list.length === 0) {
      dispatch(profileReq({
        limit: 12,
        page: 1,
        isInit: true,
        listName: listName,
        hasProfileImage: hasProfileImageOnly,
      }));
    }
  }, [location.pathname, dispatch]);

  const paginate = useCallback((limit: number, page: number) => {
    dispatch(profileReq({
      limit,
      page,
      listName: RS.listName,
      hasProfileImage: hasProfileImageOnly,
    }));
  }, [dispatch, RS.listName, hasProfileImageOnly]);

  const lastProfileRef = useCallback((node: HTMLDivElement | null) => {
    if (RS.isLoading) return;

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting) return;

        const totalPages = Math.ceil(RS.totalItems / 12);
        if (RS.currentPage < totalPages) {
          paginate(12, RS.currentPage + 1);
        }
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px 200px 0px",
      }
    );

    if (node) {
      observerRef.current.observe(node);
    }
  }, [RS.isLoading, RS.currentPage, RS.totalItems, paginate]);

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const onProfileClick = async (user: any) => {
    const result: any = await Api.compressImage(user.img, 300, 300)
    if (!result?.error) {
      const newUser = { ...user, img: result.imageUri }
      sessionStorage.setItem('selectedProfile', JSON.stringify(newUser));
      navigate('/user/view-profile')
    } else { showToast("try again later", "error") }
  };

  useEffect(() => {
    if (sessionStorage.getItem('selectedProfile'))
      sessionStorage.removeItem('selectedProfile')
  }, [])

  const onSearch = (searchValues: any) => {
    sessionStorage.setItem('preference', JSON.stringify(searchValues))
    const payload = { limit: 12, page: 1, isInit: false, isSearchInit: true, listName: "search" };
    dispatch(profileReq(payload));
  }

  const handleHasProfileImageToggle = () => {
    const nextValue = !hasProfileImageOnly;
    const listName = isSearchPage ? "search" : isViewersPage ? "viewers" : "all";
    dispatch(profileReq({
      limit: 12,
      page: 1,
      isInit: true,
      listName,
      hasProfileImage: nextValue,
    }));
  };

  return (
    <PageWrapper>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 2,
          mb: 2,
          flexWrap: "nowrap",
          overflowX: "auto",
        }}
      >
          <FormControlLabel
            sx={{ m: 0, gap: 1, flexShrink: 0, alignItems: "center" }}
            control={
              <Switch
                checked={hasProfileImageOnly}
                onChange={handleHasProfileImageToggle}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  hasDP
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {hasProfileImageOnly ? "Showing image profiles only" : "Showing all profiles"}
                </Typography>
              </Box>
            }
          />
      </Box>
      {isSearchPage && <Search onSearch={onSearch} />}
      <br />
      <Grid container spacing={3}>
        {RS.list.map((user: any, index: number) => {
          const isLastProfile = index === RS.list.length - 1;
          return (
            <Grid
              key={`key-${index + 1}`}
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
              ref={isLastProfile ? lastProfileRef : null}
            >
              <ProfileCard onProfileClick={onProfileClick} user={user} />
            </Grid>
          );
        })}
      </Grid>
    </PageWrapper>
  );
}
